import {
  type IAgentRuntime,
  Service,
  elizaLogger,
  ModelType,
  composePromptFromState,
  parseKeyValueXml,
} from "@elizaos/core";
import {
  GoPlusManage,
  type GoPlusParamType,
  GoPlusType,
} from "../lib/GoPlusManage";
import { requestPrompt, responsePrompt } from "../templates";

// Add type for check result
type GoPlusCheckResult = Record<string, unknown>;

export interface IGoplusSecurityService extends Service {
  check(text: string): Promise<string>;
}

export class GoplusSecurityService
  extends Service
  implements IGoplusSecurityService
{
  private apiKey!: string;
  declare protected runtime: IAgentRuntime;
  getInstance(): GoplusSecurityService {
    return this;
  }
  static serviceType = "GOPLUS_SECURITY";

  async initialize(runtime: IAgentRuntime): Promise<void> {
    this.runtime = runtime;
    this.apiKey = runtime.getSetting("GOPLUS_API_KEY");
  }

  async stop(): Promise<void> {
    // No cleanup needed for this service
  }

  get capabilityDescription(): string {
    return "Provides blockchain security checks via GoPlus API for tokens, NFTs, addresses, and more";
  }

  /**
   * Connect to WebSocket and send a message
   */
  async check(text: string): Promise<string> {
    try {
      elizaLogger.log("check input text", text);
      const prompt = requestPrompt(text);
      const xmlResult = await this.runtime.useModel(ModelType.TEXT_SMALL, {
        prompt,
        stopSequences: [],
      });
      const obj = parseKeyValueXml(xmlResult) as GoPlusParamType;

      elizaLogger.log("check parsed parameters", obj);

      const goPlusManage = new GoPlusManage(this.apiKey);
      let checkResult: GoPlusCheckResult;
      switch (obj.type) {
        case GoPlusType.EVMTOKEN_SECURITY_CHECK:
          checkResult = (await goPlusManage.tokenSecurity(
            obj.network || "1",
            obj.token || "",
          )) as GoPlusCheckResult;
          break;
        case GoPlusType.SOLTOKEN_SECURITY_CHECK:
          checkResult = (await goPlusManage.solanaTokenSecurityUsingGET(
            obj.token || "",
          )) as GoPlusCheckResult;
          break;
        case GoPlusType.SUITOKEN_SECURITY_CHECK:
          checkResult = (await goPlusManage.suiTokenSecurityUsingGET(
            obj.token || "",
          )) as GoPlusCheckResult;
          break;
        case GoPlusType.RUGPULL_SECURITY_CHECK:
          checkResult = (await goPlusManage.rugpullDetection(
            obj.network || "1",
            obj.contract || "",
          )) as GoPlusCheckResult;
          break;
        case GoPlusType.NFT_SECURITY_CHECK:
          checkResult = (await goPlusManage.nftSecurity(
            obj.network || "1",
            obj.token || "",
          )) as GoPlusCheckResult;
          break;
        case GoPlusType.ADDRESS_SECURITY_CHECK:
          checkResult = (await goPlusManage.addressSecurity(
            obj.wallet || "",
          )) as GoPlusCheckResult;
          break;
        case GoPlusType.APPROVAL_SECURITY_CHECK:
          checkResult = (await goPlusManage.approvalSecurity(
            obj.network || "1",
            obj.contract || "",
          )) as GoPlusCheckResult;
          break;
        case GoPlusType.ACCOUNT_ERC20_SECURITY_CHECK:
          checkResult = (await goPlusManage.erc20ApprovalSecurity(
            obj.network || "1",
            obj.wallet || "",
          )) as GoPlusCheckResult;
          break;
        case GoPlusType.ACCOUNT_ERC721_SECURITY_CHECK:
          checkResult = (await goPlusManage.erc721ApprovalSecurity(
            obj.network || "1",
            obj.wallet || "",
          )) as GoPlusCheckResult;
          break;
        case GoPlusType.ACCOUNT_ERC1155_SECURITY_CHECK:
          checkResult = (await goPlusManage.erc1155ApprovalSecurity(
            obj.network || "1",
            obj.wallet || "",
          )) as GoPlusCheckResult;
          break;
        case GoPlusType.SIGNATURE_SECURITY_CHECK:
          checkResult = (await goPlusManage.inputDecode(
            obj.network || "1",
            obj.data || "",
          )) as GoPlusCheckResult;
          break;
        case GoPlusType.URL_SECURITY_CHECK:
          checkResult = (await goPlusManage.dappSecurityAndPhishingSite(
            obj.url || "",
          )) as GoPlusCheckResult;
          break;
        default:
          throw new Error("type is invalid");
      }

      elizaLogger.log("checkResult text", checkResult);
      const responseResult = await this.runtime.useModel(ModelType.TEXT_SMALL, {
        prompt: responsePrompt(JSON.stringify(checkResult), text),
        stopSequences: [],
      });
      const checkResponse = responseResult;
      elizaLogger.log("checkResponse text", checkResponse);
      return checkResponse;
    } catch (e) {
      elizaLogger.error(e);
      return "error";
    }
  }
}

export default GoplusSecurityService;
